import { useCallback } from "react";
import "@pulse-ui/styling/pulse-wave-lib/components/ag-theme-pulse.css";

// Package Imports
import { AgGridReact } from "ag-grid-react";

// Type Imoorts
import {
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  DateFilterModule,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridStateModule,
  iconOverrides,
  MenuItemDef,
  ModuleRegistry,
  NumberFilterModule,
  RowApiModule,
  RowGroupingDisplayType,
  RowSelectionOptions,
  RowStyle,
  RowStyleModule,
  ScrollApiModule,
  SideBarDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  TextFilterModule,
  themeAlpine,
  ValidationModule,
} from "ag-grid-community";
import {
  ClipboardModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CustomFilterModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  GroupFilterModule,
  MasterDetailModule,
  MultiFilterModule,
  RowGroupingModule,
  RowSelectionModule,
  SetFilterModule,
  SideBarModule,
  TreeDataModule,
} from "ag-grid-enterprise";
import AGLoadingOverlay from "../AGOverlays/AGLoadingOverlay/AGLoadingOverlay";

ModuleRegistry.registerModules([
  CellStyleModule,
  ClientSideRowModelApiModule,
  ClientSideRowModelModule,
  ClipboardModule,
  ColumnAutoSizeModule,
  ColumnsToolPanelModule,
  CustomFilterModule,
  ContextMenuModule,
  DateFilterModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  GridStateModule,
  GroupFilterModule,
  MasterDetailModule,
  MultiFilterModule,
  NumberFilterModule,
  RowGroupingModule,
  RowApiModule,
  RowSelectionModule,
  RowStyleModule,
  ScrollApiModule,
  SetFilterModule,
  SideBarModule,
  TextFilterModule,
  TreeDataModule,
  ValidationModule,
]);

const myTheme = themeAlpine.withPart(
  iconOverrides({
    type: "image",
    mask: true,
    icons: {
      "custom-collection-icon": {
        svg: "<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='currentColor' class='bi bi-collection' viewBox='0 0 16 16'><path d='M2.5 3.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1zm2-2a.5.5 0 0 1 0-1h7a.5.5 0 0 1 0 1zM0 13a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 16 13V6a1.5 1.5 0 0 0-1.5-1.5h-13A1.5 1.5 0 0 0 0 6zm1.5.5A.5.5 0 0 1 1 13V6a.5.5 0 0 1 .5-.5h13a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-.5.5z'/></svg>",
      },
      "custom-three-dots-icon": {
        svg: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-three-dots-vertical" viewBox="0 0 16 16"><path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/></svg>`,
      },
    },
  })
);

const AGTable = ({
  gridRef,
  rowData,
  colDefs,
  defaultColDef,
  groupDisplayType,
  autoGroupColumnDef,
  sideBar,
  animateRows,
  loading,
  detailCellRendererParams,
  masterDetail,
  quickFilterText,
  grandTotalRows,
  tableHeight = 800,
  treeData,
  containerStyle = {
    height: tableHeight,
    overflow: "auto",
  },
  rowSelection,
  rowBuffer,
  suppressColumnVirtualisation,
  suppressRowVirtualisation,
  suppressCellFocus,
  suppressRowHoverHighlight,
  debounceVerticalScrollbar,
  context,
  getContextMenuItems,
  onCellDoubleClicked,
  onFirstDataRendered,
  autoSizeStrategy = { type: "fitGridWidth" },
}: {
  gridRef: any;
  rowData: any;
  colDefs: any;
  defaultColDef: any;
  groupDisplayType: RowGroupingDisplayType;
  autoGroupColumnDef?: {
    headerName: string;
    minWidth: number;
    filter: string;
    cellRendererParams: {
      suppressCount: boolean;
    };
  };
  sideBar: string | boolean | SideBarDef | string[] | null;
  animateRows: boolean;
  loading?: boolean;
  detailCellRendererParams?: {
    detailGridOptions: {
      columnDefs: {
        headerName: string;
        field: string;
        flex: number;
        autoSize: boolean;
        resizable: boolean;
      }[];
      defaultColDef: {
        sortable: boolean;
        filter: boolean;
        resizable: boolean;
        flex: number;
      };
      domLayout: string;
      suppressHorizontalScroll: boolean;
    };
    getDetailRowData: (params: any) => void;
  };
  masterDetail?: boolean;
  quickFilterText?: string;
  grandTotalRows?: "top" | "bottom";
  tableHeight?: number;
  treeData?: boolean;
  containerStyle?: React.CSSProperties;
  rowSelection?: RowSelectionOptions;
  rowBuffer?: number;
  suppressColumnVirtualisation?: boolean;
  suppressRowVirtualisation?: boolean;
  suppressCellFocus?: boolean;
  suppressRowHoverHighlight?: boolean;
  debounceVerticalScrollbar?: boolean;
  context?: any;
  getContextMenuItems?: (params: GetContextMenuItemsParams) => (MenuItemDef | DefaultMenuItem)[];
  onCellDoubleClicked?: (params: any) => void;
  onFirstDataRendered?: () => void;
  autoSizeStrategy?: SizeColumnsToFitGridStrategy | SizeColumnsToContentStrategy;
}) => {

  const getRowStyle = (params: any): RowStyle | undefined => {
    if (params.node.level > 0) {
      return { backgroundColor: "#091229" };
    }
    return undefined;
  };

  const getRowId = useCallback(
    (params: any) => params.data?.id || params.data?.row_id || params.node?.id,
    []
  );
  return (
    <div className="ag-theme-alpine ag-theme-pulse" style={containerStyle}>
      <AgGridReact
        ref={gridRef}
        rowData={rowData}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        autoGroupColumnDef={autoGroupColumnDef || undefined}
        autoSizeStrategy={autoSizeStrategy}
        groupDisplayType={groupDisplayType}
        showOpenedGroup
        alwaysAggregateAtRootLevel
        suppressAggFuncInHeader
        sideBar={sideBar}
        suppressAutoSize
        animateRows={animateRows}
        getRowId={getRowId}
        loadingOverlayComponent={AGLoadingOverlay}
        loading={loading || false}
        detailCellRendererParams={detailCellRendererParams}
        masterDetail={masterDetail}
        quickFilterText={quickFilterText}
        grandTotalRow={grandTotalRows}
        treeData={treeData}
        treeDataChildrenField={"children"}
        getContextMenuItems={getContextMenuItems}
        theme={myTheme}
        getRowStyle={getRowStyle}
        onCellDoubleClicked={onCellDoubleClicked}
        rowSelection={rowSelection}
        rowBuffer={rowBuffer ?? 20}
        suppressColumnVirtualisation={suppressColumnVirtualisation ?? false}
        suppressRowVirtualisation={suppressRowVirtualisation ?? false}
        suppressCellFocus={suppressCellFocus ?? false}
        suppressRowHoverHighlight={suppressRowHoverHighlight ?? false}
        debounceVerticalScrollbar={debounceVerticalScrollbar ?? false}
        context={context}
        onFirstDataRendered={onFirstDataRendered}
      />
    </div>
  );
};

export default AGTable;
